// Wave 20.30 — Wind particle overlay.
//
// A canvas that sits above a Mapbox `<Map>` and paints animated
// particles advected by a wind field. The field is delivered as a
// PNG from weather.aero.chat/wind/png: R = u (signed, offset 128),
// G = v (signed, offset 128), alpha = mask (0 where the model has
// no coverage). Fixed WIND_MAX so decode is stateless.
//
// One image per "frame" — for the current-conditions map you pass
// a single URL, for session-replay you pass a manifest of frames
// with `ride_sec` so playback speed maps to the ride's duration.
//
// Canvas2D not WebGL. ~1500 particles at 30fps is well within its
// budget on a laptop and it side-steps the dependency + fallback
// dance a webgl-wind lib would add. If the desktop tab is inactive
// the browser throttles rAF, which is fine — no wasted CPU.

import React, { useCallback, useEffect, useRef } from 'react';

const TOKEN_KEY = process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token';

const PARTICLE_COUNT = 1500;
// How many advection steps a particle lives before being respawned
// at a random position. Keeps long streaks from persisting after
// they wander out of a data region.
const MAX_AGE = 90;
// Non-linear screen-pixel advection: pixels_per_frame = |u| ^ EXP
// * BASE (sign preserved). Linear (EXP=1) at the old BASE=0.35 made
// a 5 mph wind and a 30 mph wind look nearly the same — both were
// "moving". Bumping EXP > 1 widens the perceptual gap so 5 mph looks
// like a soft drift, 30 mph looks obviously windy, 50 mph whips.
// BASE calibrated so 5 mph ≈ 0.28 px/frame and 30 mph ≈ 3 px/frame
// on a mid-size viewport at 30 fps.
const SPEED_EXPONENT = 1.35;
const SPEED_BASE = 0.11;
// Alpha of the "fade layer" drawn each frame — lower value → longer
// trails. 0.03 gives comet-tails without ghosting.
const FADE_ALPHA = 0.045;

const DEFAULT_MAX_MS = 51;

interface WindMapLike {
  project: (lngLat: [number, number]) => { x: number; y: number };
  unproject: (xy: [number, number]) => { lng: number; lat: number };
  getCanvas: () => HTMLCanvasElement;
  on: (type: string, listener: () => void) => void;
  off: (type: string, listener: () => void) => void;
}

interface WindFrame {
  /** Absolute URL to the PNG for this frame. */
  url: string;
  /** BBox (W, S, E, N) the PNG covers — MUST match the bbox in the
   *  URL so the client samples pixels at the right lng/lat. Falling
   *  back to the runtime viewport bbox silently mis-samples when
   *  Mapbox's viewport differs even slightly (clamp for out-of-range
   *  bounds is the most common cause). */
  bbox: [number, number, number, number];
  /** How many seconds into the ride this frame represents. Only
   *  used for timeline replay; ignored when there's a single frame. */
  rideSec?: number;
}

interface Props {
  /** The Mapbox map instance this overlay projects onto. Must be
   *  fully loaded (styledata fired). */
  map: WindMapLike | null;
  /** Frames to loop through. One frame = static current field.
   *  Multiple frames = timeline replay. */
  frames: WindFrame[];
  /** Total ride duration for timeline replay. Ignored for
   *  single-frame mode. */
  rideDurationSec?: number;
  /** Wall-clock seconds one full loop of the ride should take
   *  in the animation. Default 30. */
  loopWallSec?: number;
  /** WIND_MAX matching the PNG encoding. Defaults to 51 m/s per
   *  the server contract. Overriden if the server bumps it. */
  windMaxMs?: number;
  /** Optional callback so parent can display "Wind t=…" in a
   *  scrubber. */
  onFrameChange?: (frameIdx: number) => void;
  /** Wave 20.38a — When defined, WindParticles renders EXACTLY this
   *  frame and skips its own auto-advance. Used by SessionReplay
   *  so scrubbing the replay clock jumps the wind field to the
   *  historical model timestep for that moment of the ride. */
  pinnedFrameIdx?: number | null;
}

interface DecodedField {
  width: number;
  height: number;
  bbox: [number, number, number, number];
  // ImageData for the pixel-level u/v lookup. Kept for the whole
  // frame's lifetime so per-particle advection avoids a decode.
  imgData: ImageData;
}

/** Decode one wind PNG into an offscreen canvas so we can read RGBA
 *  arrays with `getImageData`. Returns null on decode failure so the
 *  overlay silently skips the frame. */
async function decodeField(frame: WindFrame): Promise<DecodedField | null> {
  try {
    const jwt = localStorage.getItem(TOKEN_KEY);
    const res = await fetch(frame.url, {
      headers: jwt ? { Authorization: `Bearer ${jwt}` } : undefined,
    });
    if (!res.ok) {
      // eslint-disable-next-line no-console
      console.warn('[wind] fetch failed', res.status, frame.url);
      return null;
    }
    const blob = await res.blob();
    const bmp = await createImageBitmap(blob);
    const c = document.createElement('canvas');
    c.width = bmp.width;
    c.height = bmp.height;
    const ctx = c.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(bmp, 0, 0);
    const imgData = ctx.getImageData(0, 0, bmp.width, bmp.height);
    return { width: bmp.width, height: bmp.height, bbox: frame.bbox, imgData };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[wind] decode threw', e);
    return null;
  }
}

/** Sample the wind field at (lng, lat). Returns [u, v] in m/s, or
 *  null when the sample falls in a masked / out-of-bounds cell.
 *  Uses the FIELD's bbox (the URL bbox at fetch time), not the
 *  runtime viewport bbox — those can diverge and mis-align every
 *  sample. */
function sampleField(
  field: DecodedField,
  lng: number,
  lat: number,
  windMax: number,
): [number, number] | null {
  const [w, s, e, n] = field.bbox;
  if (lat > n || lat < s) return null;
  const east = e < w ? e + 360 : e;
  let lngWrap = lng;
  if (e < w && lngWrap < w) lngWrap += 360;
  if (lngWrap < w || lngWrap > east) return null;
  const x = Math.floor(((lngWrap - w) / (east - w)) * field.width);
  const y = Math.floor(((n - lat) / (n - s)) * field.height);
  if (x < 0 || x >= field.width || y < 0 || y >= field.height) return null;
  const idx = (y * field.width + x) * 4;
  const r = field.imgData.data[idx];
  const g = field.imgData.data[idx + 1];
  const a = field.imgData.data[idx + 3];
  if (a < 128) return null;
  const u = ((r - 128) / 128) * windMax;
  const v = ((g - 128) / 128) * windMax;
  return [u, v];
}

/** Color a particle by wind speed. Blue → cyan → yellow → red. */
function speedColor(speed: number, windMax: number): string {
  const t = Math.min(1, speed / (windMax * 0.6));
  // Piecewise between four stops
  if (t < 0.33) {
    const k = t / 0.33;
    return `rgba(80,160,255,${0.55 + 0.2 * k})`;
  } else if (t < 0.66) {
    const k = (t - 0.33) / 0.33;
    return `rgba(${Math.round(80 + 175 * k)},${Math.round(160 + 60 * k)},${Math.round(255 - 60 * k)},0.85)`;
  } else {
    const k = (t - 0.66) / 0.34;
    return `rgba(255,${Math.round(220 - 130 * k)},${Math.round(120 - 100 * k)},0.9)`;
  }
}

// Wave 20.49cv — Zoom-aware sizing. The field's span in degrees is
// a proxy for zoom. At ≥ 4° (regional view) boost = 1; every halving
// of the span adds 0.5 → 1° = 2×, 0.25° = 3×; capped at 3.2. Applied
// to stroke width and to screen-pixel advection so a zoomed-in
// route-planner loop shows bold, long streaks instead of near-static
// specks (the rider's "particles are so small" report).
export function zoomBoost(spanDeg: number): number {
  if (!(spanDeg > 0)) return 1;
  return Math.max(1, Math.min(3.2, 1 + 0.5 * Math.log2(4 / spanDeg)));
}

export default function WindParticles({
  map,
  frames,
  rideDurationSec,
  loopWallSec = 30,
  windMaxMs = DEFAULT_MAX_MS,
  onFrameChange,
  pinnedFrameIdx,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const raf = useRef<number | null>(null);
  const particles = useRef<Array<{ x: number; y: number; age: number }>>([]);
  const currentField = useRef<DecodedField | null>(null);
  const currentFrameIdx = useRef<number>(0);
  const frameStartWall = useRef<number>(performance.now());
  // Wave 20.38c — decoded-frame cache. `decodeField` fetches +
  // decodes a PNG (~10-30 ms even from cache); doing that on every
  // frame change during 240x playback either backs up the network
  // or shows the same frame the whole time. Preload all frames in
  // parallel once the manifest arrives so pinnedFrameIdx changes
  // are instant lookups.
  const decodedCache = useRef<Map<number, DecodedField>>(new Map());
  const inflightCache = useRef<Map<number, Promise<DecodedField | null>>>(new Map());

  const getDecoded = useCallback(
    async (idx: number): Promise<DecodedField | null> => {
      if (idx < 0 || idx >= frames.length) return null;
      const cached = decodedCache.current.get(idx);
      if (cached) return cached;
      const inflight = inflightCache.current.get(idx);
      if (inflight) return inflight;
      const p = decodeField(frames[idx]).then((field) => {
        if (field) decodedCache.current.set(idx, field);
        inflightCache.current.delete(idx);
        return field;
      });
      inflightCache.current.set(idx, p);
      return p;
    },
    [frames],
  );

  // Load a frame + swap it in as the current field. Cache hit =
  // instant. Cache miss = async fetch, then swap when ready.
  const loadFrame = useCallback(
    (idx: number) => {
      if (idx < 0 || idx >= frames.length) return;
      const cached = decodedCache.current.get(idx);
      if (cached) {
        currentField.current = cached;
        currentFrameIdx.current = idx;
        onFrameChange?.(idx);
        return;
      }
      getDecoded(idx).then((field) => {
        if (!field) return;
        // Only apply if we're still on the same target frame — a
        // rapid succession of loadFrame calls at high playback speed
        // otherwise causes the wrong texture to win the race.
        if (currentFrameIdx.current === idx || Math.abs(currentFrameIdx.current - idx) < 2) {
          currentField.current = field;
          currentFrameIdx.current = idx;
          onFrameChange?.(idx);
        }
      });
    },
    [frames, getDecoded, onFrameChange],
  );

  // Wave 20.38c — Preload every frame in parallel once the manifest
  // arrives. Cap the concurrency at 8 to be a good browser
  // citizen; a 15-min ride has ~90 frames so this finishes in a
  // couple wall-seconds.
  useEffect(() => {
    if (frames.length === 0) return;
    decodedCache.current = new Map();
    inflightCache.current = new Map();
    let cancelled = false;
    const CONCURRENCY = 8;
    let next = 0;
    const worker = async () => {
      while (!cancelled && next < frames.length) {
        const idx = next++;
        await getDecoded(idx);
      }
    };
    Promise.all(Array.from({ length: CONCURRENCY }, worker));
    return () => {
      cancelled = true;
    };
  }, [frames, getDecoded]);

  useEffect(() => {
    if (frames.length === 0) return;
    loadFrame(0);
    frameStartWall.current = performance.now();
  }, [frames, loadFrame]);

  // Wave 20.38a — external pin: when the parent tells us the current
  // replay frame index, jump to it and skip the auto-advance.
  useEffect(() => {
    if (pinnedFrameIdx == null) return;
    if (pinnedFrameIdx === currentFrameIdx.current) return;
    loadFrame(pinnedFrameIdx);
  }, [pinnedFrameIdx, loadFrame]);

  // Advance to next frame based on wall-clock time. Single-frame mode
  // is a no-op after the initial load.
  const maybeAdvanceFrame = useCallback(() => {
    // Pinned mode = no auto-advance; the pin effect owns which frame
    // is loaded.
    if (pinnedFrameIdx != null) return;
    if (frames.length <= 1) return;
    const wall = performance.now();
    const elapsed = (wall - frameStartWall.current) / 1000;
    // If a ride duration was supplied, tick through frames so the whole
    // ride plays in `loopWallSec`. Otherwise cycle each frame for a
    // constant wall duration.
    let nextIdx: number;
    if (rideDurationSec && rideDurationSec > 0) {
      const rideT = (elapsed / loopWallSec) * rideDurationSec;
      nextIdx = 0;
      for (let i = 0; i < frames.length; i++) {
        const fs = frames[i].rideSec ?? 0;
        if (fs <= rideT) nextIdx = i;
        else break;
      }
      if (elapsed >= loopWallSec) {
        frameStartWall.current = wall;
        nextIdx = 0;
      }
    } else {
      const perFrame = loopWallSec / frames.length;
      nextIdx = Math.floor(elapsed / perFrame) % frames.length;
    }
    if (nextIdx !== currentFrameIdx.current) {
      loadFrame(nextIdx);
    }
  }, [frames, rideDurationSec, loopWallSec, loadFrame, pinnedFrameIdx]);

  // Fit canvas to the map on mount + on every resize.
  useEffect(() => {
    if (!map) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const onResize = () => {
      const mc = map.getCanvas();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = mc.clientWidth * dpr;
      canvas.height = mc.clientHeight * dpr;
      canvas.style.width = `${mc.clientWidth}px`;
      canvas.style.height = `${mc.clientHeight}px`;
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    onResize();
    map.on('resize', onResize);
    return () => {
      map.off('resize', onResize);
    };
  }, [map]);

  // Main animation loop. Runs while `map` is defined and frames exist;
  // stops cleanly on unmount so we don't leak rAF handles.
  useEffect(() => {
    if (!map) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Initial particle spawn
    const initW = canvas.clientWidth || 300;
    const initH = canvas.clientHeight || 200;
    particles.current = Array.from({ length: PARTICLE_COUNT }, () => ({
      x: Math.random() * initW,
      y: Math.random() * initH,
      age: Math.random() * MAX_AGE,
    }));
    const tick = () => {
      maybeAdvanceFrame();

      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      // Fade layer — leaves comet-tails behind fast-moving particles.
      ctx.globalCompositeOperation = 'destination-in';
      ctx.fillStyle = `rgba(0,0,0,${1 - FADE_ALPHA})`;
      ctx.fillRect(0, 0, w, h);
      ctx.globalCompositeOperation = 'source-over';

      const field = currentField.current;
      if (!field) {
        raf.current = requestAnimationFrame(tick);
        return;
      }

      // Line width scales up a touch at wider zooms so the streaks
      // don't disappear against the map at continental scale.
      const spanDeg = Math.max(
        field.bbox[2] - field.bbox[0],
        field.bbox[3] - field.bbox[1],
      );
      const boost = zoomBoost(spanDeg);
      const lineW = spanDeg > 30 ? 1.8 : spanDeg > 5 ? 1.4 : 1.2 * boost;
      // Streak length grows with zoom too, gentler than width.
      const speedBoost = 1 + 0.6 * (boost - 1);
      ctx.lineWidth = lineW;

      for (const p of particles.current) {
        p.age++;
        if (p.age > MAX_AGE) {
          p.x = Math.random() * w;
          p.y = Math.random() * h;
          p.age = 0;
          continue;
        }
        const { lng, lat } = map.unproject([p.x, p.y]);
        const uv = sampleField(field, lng, lat, windMaxMs);
        if (!uv) {
          p.age = MAX_AGE + 1;
          continue;
        }
        const [u, v] = uv;
        // Non-linear screen-pixel advection: perceptually match
        // Beaufort-ish intuition (calm ≠ breezy ≠ gale) without
        // getting stuck in physical-scale wonkiness at low zoom.
        // `v` is meteorological north-positive so screen-y flips.
        const speed = Math.hypot(u, v);
        const scale = speed > 0 ? Math.pow(speed, SPEED_EXPONENT - 1) * SPEED_BASE * speedBoost : 0;
        const nextX = p.x + u * scale;
        const nextY = p.y - v * scale;
        ctx.strokeStyle = speedColor(speed, windMaxMs);
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
        ctx.lineTo(nextX, nextY);
        ctx.stroke();
        p.x = nextX;
        p.y = nextY;
        if (p.x < 0 || p.x > w || p.y < 0 || p.y > h) {
          p.age = MAX_AGE + 1;
        }
      }

      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => {
      if (raf.current != null) cancelAnimationFrame(raf.current);
    };
  }, [map, maybeAdvanceFrame, windMaxMs]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 2,
      }}
    />
  );
}
